import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ApplyButton from './ApplyButton';
interface TaleOfTwoOwnersSectionProps {
  scrollToCalendar?: () => void;
}
const TaleOfTwoOwnersSection = ({
  scrollToCalendar
}: TaleOfTwoOwnersSectionProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="w-full bg-black py-20 px-4" data-id="element-228">
      <div className="max-w-5xl mx-auto" data-id="element-229">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-12" data-id="element-230">
          <h2 className="text-white text-3xl md:text-4xl mb-4 typekit-heading" data-id="element-231">
            A TALE OF TWO CLINIC OWNERS
          </h2>
          <p className="text-gray-300 text-xl libre-caslon-text-regular" data-id="element-232">
            Two Owners, Same Revenue. Completely Different Future.
          </p>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.15
      }} className="mb-12" data-id="element-233">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kcHwfvoVVKz4vkNzXWpg3m/twoclinics.png" alt="Tale of Two Clinic Owners - Owner A vs Owner B" className="w-full max-w-3xl mx-auto rounded-lg" data-id="element-234" />
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.2
      }} className="text-gray-300 libre-caslon-text-regular mb-8 text-center" data-id="element-235">
          <p className="mb-6" data-id="element-236">
            Both owners run around $10M a year across 6 clinics.
          </p>
          <p className="mb-8" data-id="element-237">Both think, "We've built a real business."</p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-238">
          {/* Owner A */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-8" data-id="element-239">
            <h3 className="text-2xl text-white mb-4 typekit-heading" data-id="element-240">
              Owner A – Finds out too late
            </h3>
            <div className="space-y-4 text-gray-300 libre-caslon-text-regular" data-id="element-241">
              <p data-id="element-242">Never looks at the group through buyer eyes.</p>
              <p data-id="element-243">Lets his CPA "prep the books" when a fund shows interest.</p>
              <p data-id="element-244">
                In the first real meeting, buyers rebuild his numbers, see
                everything depends on him, spot payer/data issues and
                momentum‑based growth.
              </p>
              <p data-id="element-245">
                He gets a number that's basically 2–3x his salary and a job
                offer inside his own business for the next 3–5 years.
              </p>
              <p className="font-semibold text-white mt-6" data-id="element-246">One meeting.</p>
              <p className="font-bold" data-id="element-247">
                Fifteen–twenty years of work just to find out: "I built the
                wrong thing."
              </p>
            </div>
          </motion.div>
          {/* Owner B */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="bg-gradient-to-b from-[#1a1a0a] to-[#0a0a0a] border border-[#ffdf07]/30 rounded-lg p-8" data-id="element-248">
            <h3 className="text-2xl text-[#ffdf07] mb-4 typekit-heading" data-id="element-249">
              Owner B – Plays the 8‑figure game on purpose
            </h3>
            <div className="space-y-4 text-gray-300 libre-caslon-text-regular" data-id="element-250">
              <p data-id="element-251">2–3 years earlier, runs an 8‑Figure Clinic MRI™.</p>
              <p data-id="element-252">
                Sees exactly where profit, ops, growth, payer and data are off
                from what serious money wants.
              </p>
              <p data-id="element-253">
                Spends 12–24 months fixing the right problems in the right order
                using the 30/90/24 plan.
              </p>
              <p className="font-semibold text-white mt-6" data-id="element-254">
                When buyers show up, the story is completely different:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-255">
                <li data-id="element-256">Clean, believable earnings</li>
                <li data-id="element-257">Systems that can carry 2–3x volume</li>
                <li data-id="element-258">Controllable growth</li>
                <li data-id="element-259">De‑risked payer/data</li>
              </ul>
              <p className="mt-6" data-id="element-260">
                Now the question isn't "Can we get this cheap?"
              </p>
              <p className="font-bold text-[#ffdf07]" data-id="element-261">
                It's "What terms do we need to put on the table to win this
                deal?"
              </p>
            </div>
          </motion.div>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.5
      }} className="text-center" data-id="element-262">
          <p className="text-gray-300 libre-caslon-text-regular mb-4" data-id="element-263">
            Same revenue. Same number of locations.
          </p>
          <p className="text-gray-300 libre-caslon-text-regular mb-4" data-id="element-264">
            One owner feels like he wasted 20 years.
          </p>
          <p className="text-gray-300 libre-caslon-text-regular mb-8" data-id="element-265">
            The other has real options and a story he's proud to tell.
          </p>
          <p className="text-xl text-[#ffdf07] typekit-heading" data-id="element-266">
            The 8‑Figure Clinic MRI™ is the fork in the road between those two
            futures.
          </p>
        </motion.div>
        <div className="mt-12" data-id="element-267">
          {scrollToCalendar ? <div className="flex justify-center" data-id="element-268">
              <div className="relative" data-id="element-269">
                <div className="button-border-light" data-id="element-270"></div>
                <motion.button onClick={scrollToCalendar} whileHover={{
              scale: 1.05,
              boxShadow: '0px 8px 20px rgba(255, 223, 7, 0.3)'
            }} className="relative z-10 bg-gradient-to-r from-[#ffdf07] via-[#fae651] to-[#ffdf07] text-black font-semibold py-3 px-8 sm:px-10 rounded-md text-base shadow-[0_4px_14px_rgba(255,223,7,0.25)] border border-[#ffd70077] transition-all duration-300" data-id="element-271">
                  Book My Strategy Call
                </motion.button>
              </div>
            </div> : <ApplyButton data-id="element-272" />}
        </div>
      </div>
    </section>;
};
export default TaleOfTwoOwnersSection;